/**
 * La barrita que aparece sobre un conector seleccionado, para ajustarlo sin
 * salir del canvas: puntas de flecha, forma de la ruta y etiqueta.
 *
 * Mismo patrón y misma razón que `CardActions`: el click sobre el canvas ya
 * significa otra cosa (seleccionar, arrastrar, marquesina), así que un menú que
 * se abriera ahí habría que cerrarlo antes de poder mover nada. Esto acompaña a
 * la selección y desaparece con ella.
 */

import { type EdgeEnd, type EdgeRoute } from "../model/types.ts";

/** Separación entre la línea y la barra, en píxeles de pantalla. */
const GAP_PX = 14;

/** Combinaciones de puntas que se ofrecen, en el orden del selector. */
const END_MODES: { key: string; from: EdgeEnd; to: EdgeEnd; title: string; icon: string }[] = [
  {
    key: "to",
    from: "none",
    to: "arrow",
    title: "Arrow at the end",
    icon: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h15"/><path d="m14 8 4 4-4 4"/></svg>',
  },
  {
    key: "both",
    from: "arrow",
    to: "arrow",
    title: "Arrows at both ends",
    icon: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 12h12"/><path d="m10 8-4 4 4 4"/><path d="m14 8 4 4-4 4"/></svg>',
  },
  {
    key: "none",
    from: "none",
    to: "none",
    title: "No arrows",
    icon: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M4 12h16"/></svg>',
  },
];

const ROUTES: { route: EdgeRoute; title: string; icon: string }[] = [
  {
    route: "elbow",
    title: "Elbow",
    icon: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h8v12h8"/></svg>',
  },
  {
    route: "straight",
    title: "Straight",
    icon: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M4 19 20 5"/></svg>',
  },
  {
    route: "curved",
    title: "Curved",
    icon: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M4 18C4 9 12 6 20 6"/></svg>',
  },
];

/** Misma T que la herramienta de texto: promete "acá se escribe". */
const LABEL_ICON =
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 7 5 4 19 4 19 7"/><line x1="12" y1="4" x2="12" y2="20"/><line x1="9" y1="20" x2="15" y2="20"/></svg>';

export interface EdgeActionsCallbacks {
  onEnds: (fromEnd: EdgeEnd, toEnd: EdgeEnd) => void;
  onRoute: (route: EdgeRoute) => void;
  /** El usuario quiere editar la etiqueta del conector seleccionado. */
  onLabel: () => void;
}

export interface EdgeActionsState {
  /** Punto medio de la arista, en píxeles de pantalla. */
  x: number;
  y: number;
  fromEnd: EdgeEnd;
  toEnd: EdgeEnd;
  route: EdgeRoute;
}

export class EdgeActions {
  readonly root: HTMLElement;
  private endButtons = new Map<string, HTMLElement>();
  private routeButtons = new Map<EdgeRoute, HTMLElement>();
  /** Última posición aplicada, para no tocar el DOM en cada frame sin motivo. */
  private lastLeft = NaN;
  private lastTop = NaN;
  private visible = false;

  constructor(cb: EdgeActionsCallbacks) {
    this.root = document.createElement("div");
    this.root.className = "edge-actions";
    this.root.hidden = true;

    for (const m of END_MODES) {
      const btn = this.button(m.icon, m.title, () => cb.onEnds(m.from, m.to));
      this.endButtons.set(m.key, btn);
      this.root.appendChild(btn);
    }

    const sep = document.createElement("span");
    sep.className = "edge-actions-sep";
    this.root.appendChild(sep);

    for (const r of ROUTES) {
      const btn = this.button(r.icon, r.title, () => cb.onRoute(r.route));
      this.routeButtons.set(r.route, btn);
      this.root.appendChild(btn);
    }

    const labelSep = document.createElement("span");
    labelSep.className = "edge-actions-sep";
    this.root.appendChild(labelSep);

    this.root.appendChild(this.button(LABEL_ICON, "Text (Enter)", () => cb.onLabel()));
  }

  private button(icon: string, title: string, run: () => void): HTMLElement {
    const btn = document.createElement("button");
    btn.className = "edge-action";
    btn.innerHTML = icon;
    btn.title = title;
    // El canvas escucha `pointerdown` para arrastrar y para deseleccionar: sin
    // esto, apretar un botón soltaba la selección que la barra está editando.
    btn.addEventListener("pointerdown", (e) => e.stopPropagation());
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      run();
    });
    return btn;
  }

  /** Coloca la barra sobre el punto medio de la arista y marca su estado. */
  show(state: EdgeActionsState): void {
    if (!this.visible) {
      this.visible = true;
      this.root.hidden = false;
    }

    const active =
      state.fromEnd === "arrow" && state.toEnd === "arrow"
        ? "both"
        : state.toEnd === "arrow"
          ? "to"
          : state.fromEnd === "arrow"
            ? "to"
            : "none";
    for (const [key, btn] of this.endButtons) btn.classList.toggle("active", key === active);
    for (const [route, btn] of this.routeButtons) {
      btn.classList.toggle("active", route === state.route);
    }

    const left = Math.round(state.x);
    const top = Math.round(state.y - GAP_PX);
    if (left !== this.lastLeft || top !== this.lastTop) {
      this.lastLeft = left;
      this.lastTop = top;
      this.root.style.left = `${left}px`;
      this.root.style.top = `${top}px`;
    }
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.root.hidden = true;
  }

  destroy(): void {
    this.root.remove();
  }
}
